<template>
  <div class="header__wrapper" :class="{ 'is-loading': masters.globalLoading }">
    <!-- ALERTA DE MANTENIMIENTO -->
    <v-alert
      v-if="maintenanceVisible"
      class="header__alert"
      closable
      icon="mdi-alert"
      :title="$t('header.maintenanceTitle') || 'Mantenimiento'"
      color="brand"
      :rounded="0"
      :text="
        $t('header.maintenanceText') ||
        'programado mantenimiento para el próximo 01/04/2024 entre las 10:00h y las 12:00h. Disculpe las molestias.'
      "
      @click:close="maintenanceVisible = false"
    />

    <!-- PROGRESS (FIJO ARRIBA) -->
    <v-progress-linear
      :model-value="masters.globalLoading ? 0 : 100"
      :indeterminate="masters.globalLoading"
      color="brand"
      :height="5"
      class="header__progress"
      role="progressbar"
      :aria-valuemin="0"
      :aria-valuemax="100"
      :aria-valuenow="masters.globalLoading ? undefined : 100"
    />

    <!-- HEADER PRINCIPAL -->
    <header class="header v-layout" role="banner" :style="headerInlineStyles">
      <!-- Logo -->
      <div class="header__logo">
        <nuxt-link
          :to="localePath('/')"
          aria-label="Ir al inicio"
          @click.native="guardAndLoad($event, { id: 'home', path: '/' })"
        >
          <img :src="logoSrc" class="logo" alt="Logo MSC" />
        </nuxt-link>
      </div>

      <!-- Navegación Desktop -->
      <nav
        v-show="!inRoot && !isMobile"
        class="header__nav flex-1-0 ga-1"
        :class="{ 'header__nav--disabled': masters.globalLoading }"
        role="navigation"
        aria-label="Navegación principal"
      >
        <client-only>
          <nuxt-link
            v-show="hasRole('admin')"
            :to="localePath('/')"
            @click.native="guardAndLoad($event, { id: 'home', path: '/' })"
          >
            <v-btn
              icon="mdi-home-outline"
              flat
              :readonly="masters.globalLoading"
              v-ripple
            />
          </nuxt-link>

          <nuxt-link
            v-for="link in computedMenu"
            :key="link.id"
            :to="isDisabled(link.id) ? '' : localePath(link.path)"
            :exact="true"
            class="nav__button"
            active-class="nav__button--active"
            :class="{
              'nav__button--disabled':
                masters.globalLoading || isDisabled(link.id),
            }"
            @click.native="guardAndLoad($event, link)"
            v-ripple
          >
            {{ $t(link.title) }}
          </nuxt-link>
        </client-only>
      </nav>

      <!-- Acciones Desktop -->
      <div
        class="header__logout d-flex flex-row ga-1 justify-end flex-shrink-1"
      >
        <client-only v-if="!isMobile || inRoot">

          <v-btn
            @click="versionControl.setVisible(true)"
            v-if="!inRoot && hasRole('admin')"
            :icon="true"
            :readonly="masters.globalLoading"
            :title="$t('versionControl.title')"
            variant="text"
          >
            <img
              src="/img/icons/gestor-versiones-alt.svg"
              alt=""
              aria-hidden="true"
              style="width: 24px; height: 24px;"
            />
          </v-btn>

          <v-btn
            @click="serviceNow.setVisible(true)"
            v-if="!inRoot"
            :icon="true"
            :readonly="masters.globalLoading"
            :title="$t('serviceNow.create')"
            variant="text"
          >
            <v-icon color="brand" icon="mdi-comment-alert-outline" />
          </v-btn>

          <v-btn
            @click="locationSelector.setVisible(true)"
            v-if="!inRoot"
            :icon="true"
            :readonly="masters.globalLoading"
            :title="$t('locationSelector.title')"
            variant="text"
          >
            <v-icon color="brand" icon="mdi-order-bool-ascending-variant" />
          </v-btn>


          <!-- Botón de información -->
          <CommonInformation />

          <!-- Idiomas -->
          <CommonLanguage />

          <!-- Datos usuario -->
          <div class="d-flex flex-nowrap flex-row align-center">
            <div class="header__data">
              <div class="header__user ml-2" :title="login?.NombreUsuario">
                {{ login?.NombreUsuario }}
              </div>
              <div class="header__version">{{ version }}</div>
            </div>

            <!-- Logout -->
            <v-btn
              :icon="true"
              variant="text"
              size="small"
              class="logout"
              :title="$t('header.logOut') || 'Cerrar sesión'"
              @click="logout(user?.homeAccountId)"
            >
              <v-icon icon="mdi-exit-to-app" />
            </v-btn>
          </div>
        </client-only>
      </div>

      <!-- Hamburguesa Mobile -->
      <div
        v-if="isMobile && !inRoot"
        class="header__nav d-flex flex-row flex-nowrap align-center ga-1 header-auto justify-end ml-auto w-100"
      >
        <v-btn
          @click="serviceNow.setVisible(true)"
          :icon="true"
          :readonly="masters.globalLoading"
          :title="$t('serviceNow.create')"
          variant="text"
        >
          <v-icon color="brand" icon="mdi-comment-alert-outline" />
        </v-btn>

        <v-btn
          @click="locationSelector.setVisible(true)"
          :icon="true"
          :readonly="masters.globalLoading"
          :title="$t('locationSelector.title')"
          variant="text"
        >
          <v-icon color="brand" icon="mdi-order-bool-ascending-variant" />
        </v-btn>

        <CommonInformation />
        <CommonLanguage />

        <div class="header__hamburger" v-if="!inRoot">
          <v-btn
            :icon="true"
            variant="text"
            :aria-expanded="mobileOpen ? 'true' : 'false'"
            aria-controls="mobile-menu"
            :title="mobileOpen ? $t('header.closeMenu') : $t('header.openMenu')"
            @click="toggleMobile()"
          >
            <v-icon :icon="mobileOpen ? 'mdi-close' : 'mdi-menu'" />
          </v-btn>
        </div>
      </div>
    </header>

    <!-- Menú Mobile desplegable -->
    <v-slide-y-transition>
      <v-sheet
        v-if="mobileOpen && !inRoot"
        id="mobile-menu"
        class="header__mobile mt-6 overflow-y-auto"
        role="dialog"
        aria-modal="true"
        elevation="24"
        rounded="0"
        @keydown.esc="mobileOpen = false"
        max-height="calc(100vh - 69px)"
        style="overflow-y: auto; -webkit-overflow-scrolling: touch"
      >
        <nav
          class="header__mobile-nav d-flex flex-column justify-center ga-4 mx-8 py-6"
          aria-label="Navegación móvil"
        >
          <client-only>
            <nuxt-link
              :to="localePath('/')"
              @click.native="guardAndLoad($event, { id: 'home', path: '/' })"
              class="nav__button w-100 border-0 pa-0"
              active-class="nav__button--active"
              v-ripple
            >
              <v-btn
                variant="text"
                class="w-100 text-none"
                :readonly="masters.globalLoading"
                prepend-icon="mdi-home-outline icon-size-large"
                flat
                tile
              >
                {{ $t("home.go") }}
              </v-btn>
            </nuxt-link>
            <nuxt-link
              v-for="link in computedMenu"
              :to="isDisabled(link.id) ? '' : localePath(link.path)"
              :key="link.id"
              active-class="nav__button--active"
              class="nav__button w-100 pa-2"
              :exact="true"
              :class="{
                'nav__button--disabled': masters.globalLoading,
              }"
              @click.native="guardAndLoad($event, link)"
              v-ripple
            >
              {{ $t(link.title) }}
            </nuxt-link>

            <v-divider />

            <div class="px-2 text-right">
              <p class="text-subtitle-2 font-weight-bold">
                {{ login?.NombreUsuario }}
              </p>
              <p class="text-caption text-medium-emphasis">{{ version }}</p>
            </div>

            <v-btn
              color="error-darken-7"
              class="w-100 text-none"
              @click="logout(user?.homeAccountId)"
            >
              <v-icon class="mr-2" icon="mdi-exit-to-app" />
              {{ $t("header.logOut") }}
            </v-btn>
          </client-only>
        </nav>
      </v-sheet>
    </v-slide-y-transition>
  </div>
</template>

<script setup>
/**
 * Cabecera limpia, responsiva y fija, con altura máx. 74px en desktop.
 * Mantiene tus estilos y variables SCSS. Navegación desactivable con masters.globalLoading.
 */
import { storeToRefs } from "pinia";
import { useUserStore } from "~/store/auth";
import { useMasters } from "~/store/masters";
import { useLocationSelector } from "~/store/locationSelector";
import { useVersionControl } from "~/store/versionControl";
import { useServiceNow } from "~/store/serviceNow";
import { useDisplay } from "vuetify";

const nuxtApp = useNuxtApp();
const route = useRoute();
const localePath = useLocalePath();

const masters = useMasters();
const userStore = useUserStore();
const locationSelector = useLocationSelector();
const versionControl = useVersionControl();
const serviceNow = useServiceNow();
const { user, login } = storeToRefs(userStore);

const { mdAndDown, smAndDown } = useDisplay();
const isMobile = computed(() => smAndDown.value);

const maintenanceVisible = ref(false);
const mobileOpen = ref(false);

// Menú
const menuBase = [
  { id: "scheduling", path: "/scheduling", title: "scheduling.title" },
  { id: "saturation", path: "/saturation", title: "saturation.title" },
];
const menuGeneral = [
  { id: "mates", path: "/availability", title: "mates.title" },
  { id: "crew", path: "/crew", title: "crew.title" },
];

const computedMenu = computed(() => {
  // si no es técnico, añadimos el general
  // if (!hasRole("technician")) return [...menuBase, ...menuGeneral];
  if (!hasRole("technician")) {
    return [
      menuGeneral[0], // Primer elemento de menuGeneral
      ...menuBase, // Todos los elementos de menuBase
      menuGeneral[1], // Segundo elemento de menuGeneral
    ];
  }
  return menuBase;
});

const version = computed(() => {
  const config = useRuntimeConfig();
  return config ? `v${config.public.version}` : "";
});

const inRoot = computed(() => route.path === "/" || route.path === "/en");

const logoSrc = computed(() => {
  const config = useRuntimeConfig();
  return `/img/logo/${config.public.logoImage}`;
});

function isDisabled(id) {
  // por ahora solo "saturation" deshabilitado (como en tu ejemplo)
  // return ["saturation"].includes(id);
  return false;
}

function guardAndLoad(evt, link) {
  // Si es Ctrl+click, Cmd+click o botón intermedio, dejar que el navegador abra en nueva pestaña
  if (evt && (evt.ctrlKey || evt.metaKey || evt.button === 1)) {
    return;
  }

  if (masters.globalLoading || isDisabled(link.id)) {
    evt?.preventDefault();
    evt?.stopPropagation();
    return;
  }

  if (route.path === link.path || localePath(link.path) === route.path) {
    evt?.preventDefault();
    evt?.stopPropagation();
    return;
  }

  if (mobileOpen) {
    closeMobile();
  }

  masters.globalLoading = true;
}

function toggleMobile() {
  if (masters.globalLoading) return;
  mobileOpen.value = !mobileOpen.value;
}

function closeMobile() {
  mobileOpen.value = false;
}

const headerInlineStyles = computed(() => ({
  // Fuerza altura en desktop; deja respirar en mobile
  maxHeight: isMobile.value ? undefined : "74px",
}));

const logout = (accountId) => {
  userStore.logout(accountId);
}

// Cerrar menú móvil al cambiar de ruta
watch(
  () => route.fullPath,
  () => {
    if (mobileOpen.value) mobileOpen.value = false;
  }
);

// Accesibilidad: Esc cierra menú móvil
onMounted(() => {
  const onKey = (e) => {
    if (e.key === "Escape" && mobileOpen.value) mobileOpen.value = false;
  };
  window.addEventListener("keydown", onKey);
  onBeforeUnmount(() => window.removeEventListener("keydown", onKey));
});
</script>

<style lang="scss" scoped>
@use "@/assets/scss/index.scss" as *;

.header {
  height: 90px;
}

/* Envolvente fija (reserva la franja superior y sitúa el progress) */
.header__wrapper {
  position: fixed;
  inset-inline: 0;
  top: 0;
  z-index: 10; /* sobre contenido */
  background-color: $color-light-100;
}

/* Alerta pegada arriba, sin redondeo y sin desplazar el header */
.header__alert {
  border-left: 0 !important;
  border-right: 0 !important;
  border-top: 0 !important;
  border-bottom: 1px solid rgba($color-brand-500, 0.2) !important;
}

/* Barra de progreso fija arriba de todo */
.header__progress {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
}

/* HEADER principal */
.header {
  width: 100%;
  position: fixed;
  /* deja sitio para la barra de progreso (5px) */
  top: 5px;
  display: flex;
  align-items: center;
  gap: $space-xs;
  padding: $space-xs;
  border-bottom: 1px solid $color-light-300;
  background-color: $color-light-100;

  /* control exacto de altura en desktop */
  @media (min-width: 960px) {
    height: 105px;
  }

  /* mobile: altura automática pero compacta */
  @media (max-width: 959.98px) {
    min-height: 64px;
  }

  &__logo {
    display: flex;
    align-items: center;

    .logo {
      max-width: 121px;
      height: auto;
      display: block;
    }
  }

  &__nav {
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    gap: $space-sm;
    position: relative;

    &--disabled::before {
      content: "";
      position: absolute;
      inset: -1px;
      z-index: 10;
    }
  }

  &__logout {
    display: flex;
    flex-flow: row nowrap;
    gap: $space-xs;
    align-items: center;
    justify-content: center;
    margin-left: auto;
  }

  &__data {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
  }

  &__user {
    font-weight: $font-weight-bold;
    text-align: right;
  }

  &__version {
    font-size: $font-size-xs;
    font-weight: $font-weight-regular;
  }

  &__hamburger {
    // margin-left: auto;

    @media (min-width: 960px) {
      display: none;
    }
  }
}

/* Botonera de navegación (hereda tu look&feel) */
.nav__home {
  height: 36px;
  width: 36px;
}

.nav__button {
  text-transform: uppercase;
  text-align: center;
  text-decoration: none;
  background-color: $color-light-100;
  padding: $space-xs $space-md;
  cursor: pointer;
  border: 1px solid $color-dark-700;
  width: auto;
  border-radius: $border-radius-xs;
  color: $color-dark-700;
  font-size: $font-size-sm;
  font-weight: $font-weight-bold;
  position: relative;

  &:not(.nav__button--disabled):hover {
    background-color: $color-light-300;
    border-color: $color-light-300;
  }

  &--active {
    color: $color-light-100;
    background-color: $color-brand-500;
    border-color: $color-brand-500;

    &:not(.nav__button--disabled):hover {
      background-color: $color-brand-500;
      border-color: $color-brand-500;
    }
  }

  &--disabled {
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  }
}

/* MÓVIL: panel desplegable debajo del header */
.header__mobile {
  position: fixed;
  top: calc(5px + 64px); /* progress (5px) + altura compacta aprox. en móvil */
  left: 0;
  right: 0;
  z-index: 11;
  background: $color-light-100;
  border-bottom: 1px solid $color-light-300;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);

  @media (min-width: 960px) {
    display: none;
  }

  &-nav {
    padding: $space-sm;
    display: flex;
    flex-direction: column;
    gap: $space-xs;
  }

  &-item.is-disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}

.header__divider {
  height: 1px;
  width: 100%;
  background: $color-light-300;
  margin: $space-sm 0;
}

/* Estado de carga global */
.is-loading {
  .header__nav {
    pointer-events: none;
  }
}
</style>
